import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../rootReducer'
import { CompletedStepType, ItineraryStepNumberType } from '@/src/types/itinerary'

interface ItineraryState {
    activeStep: ItineraryStepNumberType
    completedSteps: CompletedStepType
    /**
     * NX addons defer: `true` = block `getAddonsData`, `false` = allow fetch, `null` = reset / unknown.
     * Set by `useNxItineraryDeferredQueries`; read by `useSkipAddonsQueryForNxGate`.
     * Coupon fetch timing for NX is in `useCouponTray`, not this flag.
     */
    nxAddonsFetchDefer: boolean | null
    validationErrors: {
        insurance: {
            termsAndConditions: boolean
        }
    }
}
const initialState: ItineraryState = {
    activeStep: 1,
    completedSteps: [],
    nxAddonsFetchDefer: null,
    validationErrors: {
        insurance: {
            termsAndConditions: false
        }
    }
}
export const itinerarySlice = createSlice({
    name: 'itinerary',
    initialState,
    reducers: {
        setActiveStep: (state, action: PayloadAction<ItineraryStepNumberType>) => {
            state.activeStep = action.payload
        },
        addCompletedStep: (state, action: PayloadAction<ItineraryStepNumberType>) => {
            const stepToUpdate = action.payload
            const completedSteps = state.completedSteps
            if (!completedSteps.includes(stepToUpdate)) {
                state.completedSteps = [...completedSteps, stepToUpdate]
            }
        },
        removeCompletedStep: (state, action: PayloadAction<ItineraryStepNumberType>) => {
            const stepToUpdate = action.payload
            const completedSteps = state.completedSteps
            if (completedSteps.includes(stepToUpdate)) {
                state.completedSteps = completedSteps.filter(step => step !== stepToUpdate)
            }
        },
        setInsuranceTermsAndConditionsError: (state, action: PayloadAction<boolean>) => {
            state.validationErrors.insurance.termsAndConditions = action.payload
        },
        setNxAddonsFetchDefer: (state, action: PayloadAction<boolean>) => {
            state.nxAddonsFetchDefer = action.payload
        },
        resetNxAddonsFetchDefer: state => {
            state.nxAddonsFetchDefer = null
        }
    }
})

export const {
    setActiveStep,
    addCompletedStep,
    removeCompletedStep,
    setInsuranceTermsAndConditionsError,
    setNxAddonsFetchDefer,
    resetNxAddonsFetchDefer
} = itinerarySlice.actions
export const getActiveStep = (state: RootState) => state.itinerary.activeStep
export const getCompletedSteps = (state: RootState) => state.itinerary.completedSteps
export const getItineraryValidationErrors = (state: RootState) => state.itinerary.validationErrors
export const selectNxAddonsFetchDefer = (state: RootState) => state.itinerary.nxAddonsFetchDefer

export default itinerarySlice.reducer
